// App.tsx
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TaskProvider } from "./context/TaskContext";
import { UserProfileProvider } from "./context/UserProfileContext";
import { PermissionProvider, usePermission } from "./permission/PermissionContext";
import RootLayout from "./layout/RootLayout";
import Dashboard from "./pages/Dashboard";
import TaskDetail from "./pages/TaskDetail";
import MyTaskPage from "./pages/MyTask";
import TodoListPage from "./pages/MyTodoList";
import SettingsPage from "./pages/Settings";
import ServerDownPopup from "./components/ServerDownPopup";

/* ── 403 Unauthorized Page ── */
function UnauthorizedPage() {
  return (
    <div className="flex flex-col items-center justify-center h-screen bg-[#F8FAFC] gap-4 text-center px-6">
      <div className="w-20 h-20 rounded-full bg-[#FEE2E2] flex items-center justify-center text-[36px] mb-2">🔒</div>
      <h2 className="text-2xl font-bold text-[#1E293B] m-0">Access Denied</h2>
      <p className="text-sm text-[#64748B] m-0 max-w-[380px]">
        You don't have permission to access this page.<br />
        Please contact your administrator for access.
      </p>
      <a href="/my-task"
        className="mt-2 px-6 py-[10px] bg-[#6c5ffc] text-white rounded-[10px] text-sm font-semibold no-underline cursor-pointer">
        Go to My Tasks
      </a>
    </div>

  );
}

/* ── Protected Route — checks a menu permission key before rendering the page ── */
function ProtectedRoute({ permissionKey, children }: { permissionKey: string; children: React.ReactNode }) {
  const canView = usePermission(permissionKey);

  if (canView === "loading") {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F8FAFC]">
        <div className="w-9 h-9 rounded-full border-[3px] border-[#e2e8f0] border-t-[#6c5ffc] animate-spin"/>
      </div>
    );
  }

  if (canView === false) return <UnauthorizedPage />;
  return <>{children}</>;
}

export default function App() {
  return (
    <UserProfileProvider>
      <PermissionProvider>
        <TaskProvider>
          <ServerDownPopup />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/" element={<RootLayout />}>
                <Route index element={<ProtectedRoute permissionKey="analytics_dashboard"><Dashboard /></ProtectedRoute>} />
                <Route path="task/:id" element={<TaskDetail />} />
                <Route path="my-task" element={<ProtectedRoute permissionKey="task_list"><MyTaskPage /></ProtectedRoute>} />
                <Route path="my-todo" element={<ProtectedRoute permissionKey="todo_list"><TodoListPage /></ProtectedRoute>} />
                <Route path="settings" element={<ProtectedRoute permissionKey="task_configuration"><SettingsPage /></ProtectedRoute>} />
              </Route>
            </Routes>
          </BrowserRouter>
        </TaskProvider>
      </PermissionProvider>
    </UserProfileProvider>
  );
}